import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepConnector from "@mui/material/StepConnector";
import StepContent from "@mui/material/StepContent";
import StepLabel from "@mui/material/StepLabel";
import CircleIcon from "@mui/icons-material/Brightness1";
import OutlinedCircleIcon from "@mui/icons-material/PanoramaFishEye";
import PropTypes from "prop-types";
import Skeleton from "../Skeleton/Skeleton";

const useStyles = makeStyles(theme => ({
  stepper: {
    padding: "0",
    background: "none",
    paddingTop: theme.spacing(0.25)
  },
  check: {
    zIndex: "20",
    position: "absolute",
    top: theme.spacing(3.75),
    padding: theme.spacing(0),
    // Hides the divider line behind the circles
    background: `linear-gradient(${theme.palette.body}, ${theme.palette.body}) center no-repeat`,
    backgroundSize: "24px 20px"
  },
  content: {
    border: "none",
    marginTop: "0",
    flex: "1",
    padding: ({ showProgress }) =>
      theme.spacing(0, 0.2, 3, showProgress ? 2.5 : 0.2),
    marginLeft: ({ showProgress }) => theme.spacing(showProgress ? 1.5 : 0),
    [theme.breakpoints.down("md")]: {
      marginLeft: () => theme.spacing(0), // Has to be a function to get around a material-ui bug where functions override media queriess
      paddingLeft: ({ showProgress }) => theme.spacing(showProgress ? 2 : 0.2)
    }
  },
  step: {
    display: "flex",
    "&:last-of-type > span> span:last-child": {
      height: theme.spacing(0) // stops the vertical line of the last activity from extending past check mark,
    }
  },
  divider: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    width: "24px"
  },
  dividerLine: {
    height: "100%",
    width: "1px",
    backgroundColor: theme.palette.secondary[300],
    position: "absolute",
    zIndex: "1",
    top: theme.spacing(6.25)
  },
  dividerLineSearchResults: {
    display: "none"
  },
  connector: {
    padding: theme.spacing(0),
    height: "0"
  },
  connectorLine: {
    border: "none"
  }
}));

const CoursePlayerShortCourses = ({
  shortCourses,
  searchResults,
  showProgress,
  isLoading
}) => {
  const classes = useStyles({ showProgress });
  const steps = isLoading ? [...new Array(5)] : shortCourses;
  const dividerLineStyle = searchResults
    ? classes.dividerLineSearchResults
    : classes.dividerLine;

  const connector = (
    <StepConnector
      classes={{
        root: classes.connector,
        line: classes.connectorLine
      }}
    />
  );

  function getCompletionIcon(moduleCompletionStatus) {
    if (moduleCompletionStatus) {
      return <CircleIcon color="primary" />;
    } else {
      return <OutlinedCircleIcon color="action" />;
    }
  }

  return (
    <Stepper
      connector={connector}
      orientation="vertical"
      classes={{ root: classes.stepper }}
      data-qa="course-player-short-courses-stepper"
    >
      {steps.map((courseModule, index) => (
        <Step active={true} key={index} classes={{ root: classes.step }}>
          {showProgress && (
            <StepLabel
              icon={getCompletionIcon(
                courseModule && courseModule.allActivitiesCompleted
              )}
              classes={{
                root: classes.divider,
                iconContainer: classes.check,
                labelContainer: dividerLineStyle
              }}
            ></StepLabel>
          )}
          <StepContent classes={{ root: classes.content }}>
            {isLoading ? (
              <Skeleton loading={true} height={160} />
            ) : (
              courseModule && courseModule.moduleComponent
            )}
          </StepContent>
        </Step>
      ))}
    </Stepper>
  );
};

export default CoursePlayerShortCourses;

CoursePlayerShortCourses.propTypes = {
  shortCourses: PropTypes.arrayOf(
    PropTypes.shape({
      CourseModule: PropTypes.node,
      allActivitiesCompleted: PropTypes.boolean
    })
  ),
  searchResults: PropTypes.bool,
  showProgress: PropTypes.bool
};

CoursePlayerShortCourses.defaultProps = {
  showProgress: true
};
